import { isStringValue } from "@sameday/core/typePredicates";

/**
 * Stale-build guard (SD-10488), the client half of the deploy contract:
 * the build stamps `import.meta.env.VITE_BUILD_VERSION` and emits
 * `/version.json` with the same value (vite.config.ts). When the tab regains
 * visibility we compare the two (throttled); on mismatch we request a one-shot
 * full reload. Live-call blockers defer that reload until every call hosted by
 * this document has ended. Route navigation remains a retry boundary if the
 * first reload attempt was blocked.
 *
 * Old hashed chunks stay downloadable for 30 days after a deploy (SD-10437),
 * so a stale tab keeps working; this bounds HOW stale it can get.
 */

import { isJsonObject, type JsonValue } from "@sameday/core/json";

export const VERSION_CHECK_MIN_INTERVAL_MS = 60_000;

/** True when the deployed version is known, non-empty, and differs. */
export const isVersionStale = (
  currentVersion: string,
  deployedVersion: JsonValue | undefined,
): boolean =>
  isStringValue(deployedVersion) &&
  deployedVersion !== "" &&
  currentVersion !== "" &&
  deployedVersion !== currentVersion;

/** Throttle gate: first check always runs, then at most one per interval. */
export const shouldCheckVersion = (
  lastCheckedAtMs: number | null,
  nowMs: number,
  minIntervalMs: number = VERSION_CHECK_MIN_INTERVAL_MS,
): boolean =>
  lastCheckedAtMs === null || nowMs - lastCheckedAtMs >= minIntervalMs;

const fetchDeployedVersion = async (): Promise<string | null> => {
  // BASE_URL-relative (not "/version.json") so subpath deploys (/pr-N/ PR
  // previews, SD-10588) compare against their own version.json — the root
  // deploy's version would always differ and force a reload every navigation.
  const response = await fetch(`${import.meta.env.BASE_URL}version.json`, {
    cache: "no-store",
    // Bound the tail: a hung request must not dangle indefinitely, and a
    // slow failure should burn as little of the throttle window as possible.
    signal: AbortSignal.timeout(10_000),
  });
  if (!response.ok) return null;
  const body: unknown = await response.json();
  return isJsonObject(body) && isStringValue(body.version)
    ? body.version
    : null;
};

interface StaleBuildMonitor {
  /** Visibility/focus handler: throttled deployed-version fetch. */
  checkNow: () => Promise<void>;
  /** One-shot reload attempt when a newer build was detected and it is safe. */
  reloadIfStale: () => boolean;
}

// Dependencies are injectable for unit tests (tests/utils runs in node, no
// window/fetch); production callers use the defaults.
interface StaleBuildMonitorOptions {
  currentVersion?: string;
  fetchVersion?: () => Promise<string | null>;
  now?: () => number;
  reload?: () => void;
  isReloadSafe?: () => boolean;
  minIntervalMs?: number;
}

export const createStaleBuildMonitor = ({
  currentVersion = import.meta.env.VITE_BUILD_VERSION ?? "",
  fetchVersion = fetchDeployedVersion,
  now = () => Date.now(),
  reload = () => window.location.reload(),
  isReloadSafe = () => true,
  minIntervalMs = VERSION_CHECK_MIN_INTERVAL_MS,
}: StaleBuildMonitorOptions = {}): StaleBuildMonitor => {
  let lastCheckedAtMs: number | null = null;
  let stale = false;
  let reloaded = false;

  const reloadIfStale = () => {
    if (!stale || reloaded || !isReloadSafe()) return false;
    reloaded = true;
    reload();
    return true;
  };

  const checkNow = async () => {
    // No baked version (dev, misconfigured build) or already stale: nothing
    // more to learn from the network.
    if (!currentVersion || stale) return;
    if (!shouldCheckVersion(lastCheckedAtMs, now(), minIntervalMs)) return;
    // Stamped BEFORE the await on purpose: the throttle doubles as the
    // concurrent-fetch guard (rapid focus+visibility events would otherwise
    // both pass the check above). A failed attempt costs at most one
    // interval; the fetch timeout bounds how slowly it can fail.
    lastCheckedAtMs = now();
    try {
      if (isVersionStale(currentVersion, await fetchVersion())) {
        stale = true;
        // Retry here as well as on navigation and call-safety changes. The
        // final call can end while this fetch is in flight, before its safety
        // notification has any stale state to act on.
        reloadIfStale();
      }
    } catch {
      // Offline, blocked, or malformed response — the guard must never
      // disrupt the app; the next visibility change tries again.
    }
  };

  return { checkNow, reloadIfStale };
};
